import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import CustomerPicker from "../../sales/components/CustomerPicker";
import TechnicianPicker from "./TechnicianPicker";
import { useLogComplaintMutation, useLogServiceVisitMutation } from "../technicalApi";
import { useCanManageWork, useCanSearchCustomers } from "../useTechnicalPermissions";
import { TECHNICAL_SOURCE_LABEL } from "../technicalStatusMaps";
import { paths } from "../../../routes/paths";

// One modal for both COMPLAINT and SERVICE_VISIT - the two requests are the
// same shape (branch + customer + description + optional technicians), differ
// only in endpoint, permission and wording, and building two near-identical
// forms would guarantee they drift.
//
// A modal rather than a page nested in the split view's detail pane: the
// customer search and the technician multi-select both need real width, same
// call as Quotations' own create modal.
//
// Assigning technicians here is optional and, when supplied, takes the new
// row straight to ASSIGNED server-side rather than NOT_STARTED - so a
// manager taking a call can dispatch in one step. It needs Technical.ManageWork
// (the server ignores the list otherwise), so the section is hidden without it.
const COPY = {
  COMPLAINT: {
    title: "Log a Customer Complaint",
    descriptionLabel: "What the customer reported",
    placeholder: "In the customer's own words - e.g. \"remote not working since Friday\"",
    help: "Goes straight to the Technical Manager's queue. There is no approval step.",
    success: "Complaint logged",
  },
  SERVICE_VISIT: {
    title: "Log a Service Visit",
    descriptionLabel: "Visit brief",
    placeholder: "What the technician needs to do on site - e.g. \"quarterly AMC service, 6 detectors\"",
    help: "A routine visit, not tied to a complaint. If it covers an AMC service, completing it settles that scheduled visit.",
    success: "Service visit logged",
  },
};

// `presetCustomer` lets a caller that already knows whose job this is open the modal
// with the customer filled in - the AMC "Services Pending" dashboard card raises a
// visit for the customer on the row it was clicked from. Shape is CustomerPicker's own
// option ({ value, label, customer }) so the field stays a normal, changeable picker
// rather than a locked-down special case.
const LogWorkModal = ({
  show,
  sourceType,
  onClose,
  linkedFromWorkRecordGuid = null,
  linkedFromWorkNumber = null,
  presetCustomer = null,
  presetDescription = "",
}) => {
  const navigate = useNavigate();
  const canManageWork = useCanManageWork();
  const canSearchCustomers = useCanSearchCustomers();

  const [logComplaint, { isLoading: isLoggingComplaint }] = useLogComplaintMutation();
  const [logServiceVisit, { isLoading: isLoggingVisit }] = useLogServiceVisitMutation();

  const [customerOption, setCustomerOption] = useState(presetCustomer);
  const [description, setDescription] = useState(presetDescription);
  const [technicians, setTechnicians] = useState([]);

  const copy = COPY[sourceType] || COPY.COMPLAINT;
  const customer = customerOption?.customer;
  const isSaving = isLoggingComplaint || isLoggingVisit;

  // The modal is mounted once and shown by a flag, so the preset has to be applied
  // on each open - otherwise the second row clicked on the dashboard would still
  // carry the first row's customer.
  useEffect(() => {
    if (!show) return;
    setCustomerOption(presetCustomer);
    setDescription(presetDescription);
    setTechnicians([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, presetCustomer?.value, presetDescription]);

  const reset = () => {
    setCustomerOption(null);
    setDescription("");
    setTechnicians([]);
  };

  const handleClose = () => {
    if (isSaving) return;
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!customer) return toast.error("Choose the customer this is about");
    if (!description.trim()) return toast.error(`${copy.descriptionLabel} is required`);

    const technicianEmployeeRecordGuids = technicians.length ? technicians.map((t) => t.value) : undefined;

    try {
      let recordGuid;
      if (sourceType === "SERVICE_VISIT") {
        recordGuid = await logServiceVisit({
          branchId: customer.branchId,
          custId: customer.custId,
          visitDescription: description.trim(),
          linkedFromWorkRecordGuid: linkedFromWorkRecordGuid || null,
          technicianEmployeeRecordGuids,
        }).unwrap();
      } else {
        recordGuid = await logComplaint({
          branchId: customer.branchId,
          custId: customer.custId,
          complaintDescription: description.trim(),
          technicianEmployeeRecordGuids,
        }).unwrap();
      }
      toast.success(copy.success);
      reset();
      onClose();
      // The endpoint returns the new work item's guid, so land straight on it
      // - the next thing a manager does is assign or check it.
      if (recordGuid) navigate(paths.technicalWorkDetail(recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not log this work item");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static" centered>
      <Modal.Header closeButton>
        <Modal.Title>{copy.title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {linkedFromWorkNumber && (
          <div className="alert alert-info py-2">
            <small>
              Logged as a follow-up to <strong>{linkedFromWorkNumber}</strong>. The original stays closed - this is a
              new job with its own service report.
            </small>
          </div>
        )}

        <div className="mb-3">
          <label className="form-label">Customer <span className="text-danger">*</span></label>
          {/* A follow-up of an existing job keeps that job's customer - shown, not picked. */}
          {linkedFromWorkRecordGuid && presetCustomer ? (
            <div className="form-control bg-white" style={{ height: "auto" }}>
              <span className="tx-party">{presetCustomer.customer?.custName}</span>
              <span className="text-muted small"> · same customer as {linkedFromWorkNumber}</span>
            </div>
          ) : canSearchCustomers ? (
            <CustomerPicker value={customerOption} onChange={setCustomerOption} isClearable menuPortalTarget={document.body} />
          ) : (
            <div className="alert alert-warning mb-0 py-2 px-3">
              <small>This account cannot look up customers.</small>
            </div>
          )}
          {customer && (
            <small className="text-muted">
              {customer.custName} &middot; {customer.branchCode}
              {customer.mobileNo || customer.telephone ? ` · ${customer.mobileNo || customer.telephone}` : ""}
            </small>
          )}
        </div>

        <div className="mb-3">
          <label className="form-label">{copy.descriptionLabel} <span className="text-danger">*</span></label>
          <textarea
            className="form-control"
            rows={4}
            value={description}
            placeholder={copy.placeholder}
            onChange={(e) => setDescription(e.target.value)}
          />
          <small className="text-muted">{copy.help}</small>
        </div>

        {canManageWork && (
          <div className="mb-2">
            <label className="form-label">Assign technician(s) <span className="text-muted">(optional)</span></label>
            <TechnicianPicker
              branchId={customer?.branchId}
              value={technicians}
              onChange={(v) => setTechnicians(v || [])}
              isDisabled={!customer}
              menuPortalTarget={document.body}
              noBranchLabel="Pick the customer above first"
            />
            <small className="text-muted d-block mt-1">
              {/* The list is the customer's OWN branch, not the header's branch filter -
                  a job belongs to the branch that holds the customer. Saying so here
                  stops the "I selected a branch and it is still disabled" reading. */}
              {customer ? (
                <>Technicians in <strong>{customer.branchCode}</strong> - the customer&apos;s branch.</>
              ) : (
                <>Unlocks once a customer is chosen - the technician list follows the customer&apos;s branch,
                  not the branch filter at the top of the screen.</>
              )}
            </small>
            <small className="text-muted d-block mt-1">
              Assigning here dispatches immediately - the job opens as <strong>Assigned</strong> instead of Not Started.
              Leave blank to let the Technical Manager allocate it.
            </small>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSaving}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isSaving || !canSearchCustomers}>
          {isSaving ? "Saving..." : `Log ${TECHNICAL_SOURCE_LABEL[sourceType] || "Work"}`}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default LogWorkModal;
